import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Save, RotateCcw, Loader2, Settings, Cpu, Zap, Crown, MessageSquare, Sparkles } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CONFIG = {
  FUNCTIONAL: {
    model: "gpt-4o",
    max_tokens: 2000,
    temperature: 0.3,
    instruction: `INSTRUCCIÓN OUTPUT OBLIGATORIA:
- Respuesta entre 300-600 palabras
- Incluir Header con identificador [FUNC-XXX-NNN]
- KPIs con tags [DATA][CALC][BENCH][INSUF]
- Análisis conciso con cifras específicas de los datos proporcionados
- 2-3 recomendaciones priorizadas con impacto en Q
- Formato HTML visual obligatorio (NO texto plano, NO tablas ASCII)
- PROHIBIDO decir "no tengo datos" si los datos están en el input`
  },
  PREMIUM: {
    model: "gpt-4o",
    max_tokens: 2500,
    temperature: 0.5,
    instruction: `INSTRUCCIÓN OUTPUT OBLIGATORIA:
- Respuesta entre 600-800 palabras
- Incluir Header con identificador [PREM-XXX-NNN]
- KPIs con tags [DATA][CALC][BENCH][INSUF]
- Diagnóstico profundo con causas raíz basadas en los datos
- 3-5 recomendaciones con impacto Q cuantificado
- Formato HTML visual obligatorio
- Analizar como consultor McKinsey Top 1%
- PROHIBIDO respuestas genéricas sin cifras específicas`
  },
  C_LEVEL: {
    model: "gpt-4o",
    max_tokens: 12000,
    temperature: 0.7,
    instruction: `INSTRUCCIÓN OUTPUT OBLIGATORIA:
- Respuesta entre 2000-3500 palabras
- Incluir Header ejecutivo con identificador
- KPIs estratégicos con tags [DATA][CALC][BENCH]
- Análisis con escenarios (optimista/base/pesimista)
- Decisiones recomendadas con trade-offs claros
- Impacto financiero cuantificado en Q
- Timeline de implementación específico
- Responder como ejecutivo Fortune 50 con 20+ años experiencia
- Formato HTML visual obligatorio
- PROHIBIDO recomendaciones genéricas tipo "coordinar con equipo"`
  },
  CONVERSACION: {
    model: "gpt-4o",
    max_tokens: 1000,
    temperature: 0.5,
    instruction: `INSTRUCCIÓN OUTPUT OBLIGATORIA:
- Respuesta entre 200-300 palabras
- Mantener contexto de conversación anterior
- Formato HTML visual
- Ser conciso pero con datos específicos`
  },
  SUPER_100: {
    model: "gpt-4o",
    max_tokens: 15000,
    temperature: 0.6,
    instruction: `INSTRUCCIÓN OUTPUT OBLIGATORIA SUPER-AGENTE 100:

EXTENSIÓN: Respuesta entre 1500-2000 palabras (~4 páginas)
FORMATO HTML VISUAL OBLIGATORIO: Generar output con HTML inline y CSS. NO texto plano. NO tablas ASCII.

ESTRUCTURA REQUERIDA:

1. HEADER EJECUTIVO (HTML con gradiente azul #1E3A5F)
2. ALERT BANNER (semáforos: 🔴 Críticos, 🟡 Atención, 🟢 Saludables)
3. KPI CARDS (grid 4 columnas, colores pastel según estado)
4. TABLA DE DATOS (HTML con headers)
5. ANÁLISIS DETALLADO (secciones con headers)
6. RECOMENDACIONES (cards priorizadas con Q impacto, Owner, Timeline)
7. FOOTER METADATA (SUPER-100, Tiempo, Módulos consultados)

TAGS ORIGEN OBLIGATORIOS:
- [DATA] = Dato del archivo (fondo azul #DBEAFE)
- [CALC] = Calculado (fondo verde #D1FAE5)
- [BENCH] = Benchmark industria (fondo morado #E9D5FF)
- [INSUF] = No disponible (fondo gris #F1F5F9)

CONTENIDO OBLIGATORIO:
- Mínimo 8 KPIs con semáforo
- Mínimo 1 tabla de datos específicos
- Mínimo 3 recomendaciones priorizadas con Q impacto
- Análisis profundo con cifras del archivo
- PROHIBIDO respuestas genéricas sin datos
- PROHIBIDO decir "no tengo información"

MÓDULOS CONSULTADOS: RVM, FIN, OPS, SCH, LOG, HRH`
  }
};

const AGENT_TYPE_INFO = {
  FUNCTIONAL: {
    icon: Cpu,
    label: "Funcional",
    description: "Procesa datos, ejecuta cálculos y genera métricas",
    color: "bg-blue-500"
  },
  PREMIUM: {
    icon: Zap,
    label: "Premium",
    description: "Predicciones, análisis avanzado y tendencias",
    color: "bg-amber-500"
  },
  C_LEVEL: {
    icon: Crown,
    label: "C-Level",
    description: "Proyecciones estratégicas y análisis ejecutivo",
    color: "bg-red-500"
  },
  CONVERSACION: {
    icon: MessageSquare,
    label: "Conversación",
    description: "Interacción conversacional con contexto",
    color: "bg-green-500"
  },
  SUPER_100: {
    icon: Sparkles,
    label: "SUPER-100",
    description: "Super Agente orquestador que consolida análisis de todos los módulos",
    color: "bg-purple-500"
  }
};

const AgentTypeConfig = () => {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [activeType, setActiveType] = useState('FUNCTIONAL');

  // Fetch existing config
  const { data: existingConfig, isLoading } = useQuery({
    queryKey: ['agent-type-config'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/agent-type-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  });

  // Load config when data is fetched
  useEffect(() => {
    if (existingConfig) {
      setConfig(prev => ({
        ...DEFAULT_CONFIG,
        ...existingConfig
      }));
    }
  }, [existingConfig]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (configData) => {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/admin/agent-type-config`, configData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Configuración guardada correctamente');
      queryClient.invalidateQueries(['agent-type-config']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    }
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/agent-type-config/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      setConfig(DEFAULT_CONFIG);
      toast.success('Configuración restaurada a valores por defecto');
      queryClient.invalidateQueries(['agent-type-config']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al restaurar configuración');
    }
  });

  const updateTypeConfig = (type, field, value) => {
    setConfig(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    saveMutation.mutate(config);
  };

  const handleReset = () => {
    if (window.confirm('¿Estás seguro de restaurar la configuración a valores por defecto?')) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const TypeInfo = AGENT_TYPE_INFO[activeType];
  const TypeIcon = TypeInfo?.icon || Settings;

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-white">Configuración de Agentes por Tipo</CardTitle>
                <CardDescription className="text-slate-400">
                  Define modelo, tokens, temperatura e instrucciones para cada tipo de agente
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={resetMutation.isPending}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                {resetMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Restaurar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Cambios
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeType} onValueChange={setActiveType}>
            <TabsList className="grid grid-cols-5 bg-slate-900/50 mb-6">
              {Object.entries(AGENT_TYPE_INFO).map(([type, info]) => {
                const Icon = info.icon;
                return (
                  <TabsTrigger
                    key={type}
                    value={type}
                    className="flex items-center gap-2 data-[state=active]:bg-slate-700"
                  >
                    <Icon className="w-4 h-4" />
                    {info.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {Object.keys(AGENT_TYPE_INFO).map((type) => {
              const typeInfo = AGENT_TYPE_INFO[type];
              const typeConfig = config[type] || DEFAULT_CONFIG[type];
              
              return (
                <TabsContent key={type} value={type} className="space-y-6">
                  {/* Type Description */}
                  <div className={`p-4 rounded-lg ${typeInfo.color}/10 border border-${typeInfo.color.replace('bg-', '')}/30`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                    </div>
                    <p className="text-slate-400 text-sm">{typeInfo.description}</p>
                  </div>

                  {/* Configuration Fields */}
                  <div className="grid grid-cols-3 gap-4">
                    {/* Model */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Modelo LLM</Label>
                      <Select
                        value={typeConfig.model}
                        onValueChange={(value) => updateTypeConfig(type, 'model', value)}
                      >
                        <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                          <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                          <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
                          <SelectItem value="claude-sonnet-4-20250514">Claude Sonnet 4</SelectItem>
                          <SelectItem value="claude-3-haiku-20240307">Claude Haiku</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Max Tokens */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Max Tokens de Output</Label>
                      <Input
                        type="number"
                        value={typeConfig.max_tokens}
                        onChange={(e) => updateTypeConfig(type, 'max_tokens', parseInt(e.target.value) || 2000)}
                        className="bg-slate-900 border-slate-600 text-white"
                        min={500}
                        max={20000}
                      />
                      <p className="text-xs text-slate-500">
                        Rango: 500 - 20,000 | Actual: <span className="text-blue-400 font-semibold">{typeConfig.max_tokens?.toLocaleString()}</span> tokens (~{Math.round(typeConfig.max_tokens * 0.75)} palabras)
                      </p>
                    </div>

                    {/* Temperature */}
                    <div className="space-y-2">
                      <Label className="text-slate-300">Temperature</Label>
                      <Input
                        type="number"
                        value={typeConfig.temperature}
                        onChange={(e) => updateTypeConfig(type, 'temperature', parseFloat(e.target.value) || 0.5)}
                        className="bg-slate-900 border-slate-600 text-white"
                        min={0}
                        max={1}
                        step={0.1}
                      />
                      <p className="text-xs text-slate-500">0 = Determinístico, 1 = Creativo</p>
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className="space-y-2">
                    <Label className="text-slate-300">Instrucciones de Output</Label>
                    <Textarea
                      value={typeConfig.instruction}
                      onChange={(e) => updateTypeConfig(type, 'instruction', e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white min-h-[200px] font-mono text-sm"
                      placeholder="Define las instrucciones específicas para este tipo de agente..."
                    />
                    <p className="text-xs text-slate-500">
                      Estas instrucciones se agregarán al prompt de cada agente de tipo {typeInfo.label}
                    </p>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>

      {/* Configuration Summary */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Resumen de Configuración - Límites de Output</CardTitle>
          <CardDescription className="text-slate-400">
            Los tokens determinan la extensión máxima de las respuestas de cada tipo de agente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            {Object.entries(config).filter(([key]) => key !== 'updated_at' && key !== 'updated_by').map(([type, typeConfig]) => {
              const typeInfo = AGENT_TYPE_INFO[type];
              if (!typeInfo) return null;
              const Icon = typeInfo.icon;
              const tokens = typeConfig?.max_tokens || 0;
              const palabras = Math.round(tokens * 0.75);
              
              // Color based on token limit
              const tokenColorClass = tokens >= 10000 ? 'text-purple-400' : 
                                      tokens >= 5000 ? 'text-blue-400' : 
                                      tokens >= 2000 ? 'text-green-400' : 'text-slate-400';
              
              return (
                <div key={type} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded ${typeInfo.color}`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white font-medium">{typeInfo.label}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Modelo:</span>
                      <span className="text-slate-300 text-xs">{typeConfig?.model}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Max Tokens:</span>
                      <span className={`font-bold ${tokenColorClass}`}>{tokens?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">≈ Palabras:</span>
                      <span className="text-slate-300">{palabras?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Temperatura:</span>
                      <span className="text-slate-300">{typeConfig?.temperature}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentTypeConfig;
